// Pre-composed section templates.
//
// Deliberately built LAST, after the motion, hover, layout, icon and widget
// layers landed — a template library authored against the old ceiling would
// have baked that ceiling in as "what a section looks like here". Every
// template below therefore uses the new capability on purpose: real vector
// icons (not emoji), staggered reveals, hover states, full-bleed bands,
// shape dividers, decorative layers and anchors.
//
// They are ordinary `Section` objects, so nothing about them is special at
// render time — inserting one is the same operation as building it by hand,
// and the author can then edit every part of it.

import type { Block, Section } from './types'

export interface SectionTemplate {
  id: string
  name: string
  description: string
  category: 'Hero' | 'Features' | 'Social proof' | 'Pricing' | 'Content' | 'Conversion' | 'Footer'
  build: () => Section
}

// Ids must be unique per page. The designer regenerates them on insert, but
// generating fresh ones here too means a template inserted twice can never
// collide even if that step is skipped.
let seq = 0
const uid = (p: string) => `${p}_${Date.now().toString(36)}${(seq++).toString(36)}`

/** The reveal used across templates — one motion vocabulary, not six. */
const reveal = (stagger?: number): Block['animation'] => ({
  preset: 'fade-up',
  on: 'scroll',
  duration: 650,
  once: true,
  ...(stagger ? { stagger } : {}),
})

/** The card-hover used across templates. */
const cardHover: NonNullable<Block['style']>['hover'] = { translateY: -4, shadow: 'lg' }

export const SECTION_TEMPLATES: SectionTemplate[] = [
  // ── Hero ────────────────────────────────────────────────────────────
  {
    id: 'hero-centered',
    name: 'Centered hero',
    description: 'Full-screen gradient band, eyebrow, headline, dual CTA, wave divider.',
    category: 'Hero',
    build: () => ({
      id: uid('sec'),
      name: 'Hero',
      tag: 'custom',
      anchorId: 'top',
      width: 'full',
      minHeight: 'screen',
      verticalAlign: 'center',
      dividerBottom: { kind: 'wave', height: 80 },
      decorations: [
        { id: uid('deco'), kind: 'glow', corner: 'top-right', size: 460, opacity: 0.22 },
        { id: uid('deco'), kind: 'dot-grid', corner: 'bottom-left', size: 380, opacity: 0.1 },
      ],
      style: {
        background: {
          kind: 'gradient_animated',
          gradientFrom: 'var(--pg-primary)',
          gradientTo: 'var(--pg-accent)',
          gradientAngle: 135,
        },
      },
      blocks: [
        {
          id: uid('h'), type: 'hero',
          eyebrow: 'New',
          title: 'Build the site your product deserves',
          subtitle: 'Design, publish and iterate — without waiting on a developer.',
          align: 'center', height: 'normal',
          background: { kind: 'none' },
          textColor: 'var(--pg-on-primary)',
          imageSide: 'none',
          animation: reveal(),
          primaryCta: {
            id: uid('btn'), label: 'Get started', variant: 'secondary',
            on_click: { kind: 'scroll_to', target: 'pricing', smooth: true },
          },
          secondaryCta: {
            id: uid('btn'), label: 'See features', variant: 'ghost',
            on_click: { kind: 'scroll_to', target: 'features', smooth: true },
          },
        } as Block,
      ],
    }),
  },
  {
    id: 'hero-split',
    name: 'Split hero with logos',
    description: 'Headline beside an image, with a scrolling logo marquee beneath.',
    category: 'Hero',
    build: () => ({
      id: uid('sec'), name: 'Hero', tag: 'custom', anchorId: 'top',
      blocks: [
        {
          id: uid('h'), type: 'hero',
          eyebrow: 'Platform',
          title: 'Everything you need, in one place',
          subtitle: 'A complete toolkit for teams that ship.',
          align: 'left', height: 'tall',
          background: { kind: 'none' },
          imageSide: 'right', imageUrl: '',
          animation: reveal(),
          primaryCta: { id: uid('btn'), label: 'Start free', variant: 'primary' },
        } as Block,
        {
          id: uid('lc'), type: 'logo_cloud',
          title: 'Trusted by teams everywhere',
          mode: 'marquee', marqueeSpeed: 32, monochrome: true, logoHeight: 28,
          items: Array.from({ length: 6 }, () => ({ id: uid('lg'), url: '', name: 'Logo' })),
        } as Block,
      ],
    }),
  },

  // ── Features ────────────────────────────────────────────────────────
  {
    id: 'features-bento',
    name: 'Bento feature grid',
    description: 'Asymmetric tile grid with vector icons and staggered reveal.',
    category: 'Features',
    build: () => ({
      id: uid('sec'), name: 'Features', tag: 'custom', anchorId: 'features',
      blocks: [
        {
          id: uid('bg'), type: 'bento_grid', columns: 4, gap: 16,
          eyebrow: 'Why us', heading: 'Built for the way you work',
          animation: reveal(80),
          cells: [
            { id: uid('bc'), span: 2, rowSpan: 2, icon: 'zap', title: 'Fast by default', body: 'Publish in minutes, not weeks.' },
            { id: uid('bc'), span: 2, icon: 'shield', title: 'Secure', body: 'Role-based access on every object.' },
            { id: uid('bc'), span: 1, icon: 'bar-chart', title: 'Analytics' },
            { id: uid('bc'), span: 1, icon: 'globe', title: 'Global' },
          ],
        } as Block,
      ],
    }),
  },
  {
    id: 'features-cards',
    name: 'Feature cards',
    description: 'Three-up icon cards that lift on hover, revealed in sequence.',
    category: 'Features',
    build: () => ({
      id: uid('sec'), name: 'Features', tag: 'custom', anchorId: 'features',
      blocks: [
        {
          id: uid('fg'), type: 'feature_grid', columns: 3, align: 'left', iconStyle: 'circle',
          eyebrow: 'Capabilities', heading: 'Everything you need',
          subheading: 'A comprehensive toolkit to help your team move faster.',
          animation: reveal(90),
          style: { hover: cardHover, transition: 220 },
          items: [
            { id: uid('fi'), icon: 'rocket', title: 'Ship faster', description: 'Go from idea to live page in one sitting.' },
            { id: uid('fi'), icon: 'users', title: 'Work together', description: 'Design, review and publish as a team.' },
            { id: uid('fi'), icon: 'trending-up', title: 'Grow', description: 'Measure what works and iterate on it.' },
          ],
        } as Block,
      ],
    }),
  },
  {
    id: 'features-steps',
    name: 'How it works',
    description: 'Numbered process steps with connectors.',
    category: 'Features',
    build: () => ({
      id: uid('sec'), name: 'How it works', tag: 'custom', anchorId: 'how',
      style: { background: { kind: 'color', color: 'var(--pg-surface)' } },
      width: 'full',
      blocks: [
        {
          id: uid('st'), type: 'steps', layout: 'horizontal', numbered: true, align: 'center',
          eyebrow: 'Process', heading: 'Three steps to launch',
          animation: reveal(120),
          steps: [
            { id: uid('s'), title: 'Describe it', description: 'Tell us what you want to build.', icon: 'pencil' },
            { id: uid('s'), title: 'Review', description: 'Tweak the design to taste.', icon: 'eye' },
            { id: uid('s'), title: 'Publish', description: 'Go live on your own domain.', icon: 'rocket' },
          ],
        } as Block,
      ],
    }),
  },

  // ── Social proof ────────────────────────────────────────────────────
  {
    id: 'proof-stats',
    name: 'Counting stats',
    description: 'Figures that count up as they scroll into view.',
    category: 'Social proof',
    build: () => ({
      id: uid('sec'), name: 'By the numbers', tag: 'custom',
      width: 'full',
      style: { background: { kind: 'color', color: 'var(--pg-surface-alt)' } },
      blocks: [
        {
          id: uid('ct'), type: 'counter', columns: 4, align: 'center', separator: true,
          heading: 'Trusted at scale',
          animation: reveal(),
          items: [
            { id: uid('c'), value: 12000, suffix: '+', label: 'Customers' },
            { id: uid('c'), value: 99.9, decimals: 1, suffix: '%', label: 'Uptime' },
            { id: uid('c'), value: 40, prefix: '<', suffix: 'ms', label: 'Latency' },
            { id: uid('c'), value: 24, suffix: '/7', label: 'Support' },
          ],
        } as Block,
      ],
    }),
  },
  {
    id: 'proof-testimonials',
    name: 'Testimonial wall',
    description: 'A grid of customer quotes with ratings.',
    category: 'Social proof',
    build: () => ({
      id: uid('sec'), name: 'Testimonials', tag: 'custom', anchorId: 'testimonials',
      blocks: [
        {
          id: uid('tm'), type: 'testimonial', mode: 'wall', columns: 3, align: 'left',
          quote: '', authorName: '',
          animation: reveal(90),
          style: { hover: cardHover, transition: 220 },
          items: [
            { id: uid('q'), quote: 'This changed how our team ships.', authorName: 'Jane Doe', authorRole: 'Head of Product', rating: 5 },
            { id: uid('q'), quote: 'We launched in a week instead of a quarter.', authorName: 'Alex Kim', authorRole: 'CTO', rating: 5 },
            { id: uid('q'), quote: 'The one tool everyone actually uses.', authorName: 'Sam Patel', authorRole: 'Design Lead', rating: 5 },
          ],
        } as Block,
      ],
    }),
  },
  {
    id: 'proof-team',
    name: 'Team',
    description: 'Team member cards with photos, roles and social links.',
    category: 'Social proof',
    build: () => ({
      id: uid('sec'), name: 'Team', tag: 'custom', anchorId: 'team',
      blocks: [
        {
          id: uid('tm'), type: 'team', columns: 4, shape: 'circle', align: 'center',
          eyebrow: 'Our team', heading: 'The people behind it',
          animation: reveal(80),
          members: [
            { id: uid('m'), name: 'Jane Doe', role: 'Founder & CEO' },
            { id: uid('m'), name: 'Alex Kim', role: 'Engineering' },
            { id: uid('m'), name: 'Sam Patel', role: 'Design' },
            { id: uid('m'), name: 'Riya Shah', role: 'Customer Success' },
          ],
        } as Block,
      ],
    }),
  },

  // ── Pricing ─────────────────────────────────────────────────────────
  {
    id: 'pricing-toggle',
    name: 'Pricing with period toggle',
    description: 'Three tiers with a monthly/yearly switch and a highlighted plan.',
    category: 'Pricing',
    build: () => ({
      id: uid('sec'), name: 'Pricing', tag: 'custom', anchorId: 'pricing',
      blocks: [
        {
          id: uid('pr'), type: 'pricing', columns: 3,
          heading: 'Simple, transparent pricing',
          subheading: 'Start free. Pay only when you need more.',
          showPeriodToggle: true, periodLabel: 'Monthly', altPeriodLabel: 'Yearly', altPeriodBadge: 'Save 20%',
          animation: reveal(80),
          tiers: [
            { id: uid('t'), name: 'Starter', price: '$0', period: '/mo', altPrice: '$0', altPeriod: '/yr', features: ['1 project', 'Community support'], cta: { id: uid('b'), label: 'Start free', variant: 'outline' } },
            { id: uid('t'), name: 'Pro', price: '$29', period: '/mo', altPrice: '$278', altPeriod: '/yr', highlighted: true, badge: 'Most popular', features: ['Unlimited projects', 'Custom domain', 'Priority support'], cta: { id: uid('b'), label: 'Choose Pro', variant: 'primary' } },
            { id: uid('t'), name: 'Team', price: '$99', period: '/mo', altPrice: '$950', altPeriod: '/yr', features: ['Everything in Pro', 'SSO', 'Audit log'], cta: { id: uid('b'), label: 'Contact sales', variant: 'outline' } },
          ],
        } as Block,
      ],
    }),
  },
  {
    id: 'pricing-compare',
    name: 'Plan comparison',
    description: 'Feature matrix with check / cross cells and a highlighted column.',
    category: 'Pricing',
    build: () => ({
      id: uid('sec'), name: 'Compare', tag: 'custom',
      blocks: [
        {
          id: uid('cmp'), type: 'comparison_table', stickyHeader: true,
          heading: 'Compare plans',
          animation: reveal(),
          columns: [
            { id: uid('c'), label: 'Starter' },
            { id: uid('c'), label: 'Pro', highlighted: true, badge: 'Popular' },
            { id: uid('c'), label: 'Team' },
          ],
          rows: [
            { id: uid('r'), label: 'Projects', values: ['1', '10', 'Unlimited'], group: 'Basics' },
            { id: uid('r'), label: 'Custom domain', values: [false, true, true] },
            { id: uid('r'), label: 'SSO', values: [false, false, true], group: 'Security' },
            { id: uid('r'), label: 'Audit log', values: [false, false, true] },
          ],
        } as Block,
      ],
    }),
  },

  // ── Content ─────────────────────────────────────────────────────────
  {
    id: 'content-faq',
    name: 'FAQ',
    description: 'Collapsible question and answer list.',
    category: 'Content',
    build: () => ({
      id: uid('sec'), name: 'FAQ', tag: 'custom', anchorId: 'faq',
      blocks: [
        {
          id: uid('faq'), type: 'faq', heading: 'Frequently asked questions',
          animation: reveal(),
          items: [
            { id: uid('q'), question: 'How does it work?', answer: 'Describe the flow in a sentence or two.' },
            { id: uid('q'), question: 'Can I cancel anytime?', answer: 'Yes — no commitments.' },
            { id: uid('q'), question: 'Do you offer support?', answer: 'Email support is included on all plans.' },
          ],
        } as Block,
      ],
    }),
  },
  {
    id: 'content-gallery',
    name: 'Masonry gallery',
    description: 'Mixed-height image grid with a click-to-zoom lightbox.',
    category: 'Content',
    build: () => ({
      id: uid('sec'), name: 'Gallery', tag: 'custom', anchorId: 'gallery',
      blocks: [
        {
          id: uid('gal'), type: 'gallery', columns: 3, layout: 'masonry',
          lightbox: true, rounded: true, gap: 12, hoverEffect: 'zoom',
          animation: reveal(60),
          items: Array.from({ length: 6 }, () => ({ id: uid('gi'), url: '', caption: '' })),
        } as Block,
      ],
    }),
  },

  // ── Conversion ──────────────────────────────────────────────────────
  {
    id: 'cta-band',
    name: 'CTA band',
    description: 'Full-bleed call-to-action with a slanted divider.',
    category: 'Conversion',
    build: () => ({
      id: uid('sec'), name: 'Call to action', tag: 'custom',
      width: 'full',
      dividerTop: { kind: 'slant', height: 70 },
      decorations: [{ id: uid('deco'), kind: 'blob', corner: 'bottom-right', size: 420, opacity: 0.2 }],
      style: { background: { kind: 'color', color: 'var(--pg-primary)' } },
      blocks: [
        {
          id: uid('cta'), type: 'cta_banner',
          title: 'Ready to get started?',
          subtitle: 'Start your free 14-day trial — no credit card required.',
          align: 'center',
          background: { kind: 'none' },
          textColor: 'var(--pg-on-primary)',
          animation: reveal(),
          primaryCta: { id: uid('b'), label: 'Start free', variant: 'secondary' },
          secondaryCta: { id: uid('b'), label: 'Talk to sales', variant: 'ghost' },
        } as Block,
      ],
    }),
  },
  {
    id: 'cta-contact',
    name: 'Contact form',
    description: 'Contact form that stores submissions, with spam guard and a success message.',
    category: 'Conversion',
    build: () => ({
      id: uid('sec'), name: 'Contact', tag: 'custom', anchorId: 'contact',
      blocks: [
        {
          id: uid('f'), type: 'form',
          heading: 'Get in touch',
          subheading: 'We usually reply within one business day.',
          submitLabel: 'Send message',
          layout: 'stacked', align: 'left',
          animation: reveal(),
          // Storing is the DEFAULT — no author wiring needed for the form to
          // actually go somewhere.
          destination: 'submissions',
          spam: { honeypot: true, minSeconds: 2 },
          success: { title: 'Thanks!', message: "We've got your message and will be in touch shortly." },
          fields: [
            { id: uid('fd'), name: 'name', label: 'Name', type: 'text', placeholder: 'Your name', required: true },
            { id: uid('fd'), name: 'email', label: 'Email', type: 'email', placeholder: 'you@example.com', required: true },
            { id: uid('fd'), name: 'message', label: 'Message', type: 'textarea', placeholder: 'How can we help?', required: true },
          ],
        } as Block,
      ],
    }),
  },

  // ── Footer ──────────────────────────────────────────────────────────
  {
    id: 'footer-columns',
    name: 'Multi-column footer',
    description: 'Brand column, two link columns and social icons — a real footer starting point.',
    category: 'Footer',
    build: () => ({
      id: uid('sec'), name: 'Footer', tag: 'footer',
      width: 'full',
      style: {
        background: { kind: 'color', color: 'var(--pg-surface)' },
        padding: { top: 48, right: 0, bottom: 32, left: 0 },
      },
      blocks: [
        {
          id: uid('cols'), type: 'columns', count: 4, gap: 32, align: 'top',
          columns: [
            {
              id: uid('col'), width: '2fr',
              blocks: [
                { id: uid('hd'), type: 'heading', level: 3, text: 'Your company' } as Block,
                { id: uid('p'), type: 'paragraph', text: 'One line about what you do and who it is for.' } as Block,
                {
                  id: uid('so'), type: 'social_links', mode: 'follow', variant: 'circle', iconSize: 18, align: 'left',
                  links: [
                    { id: uid('sl'), network: 'x', url: 'https://x.com/' },
                    { id: uid('sl'), network: 'linkedin', url: 'https://linkedin.com/' },
                    { id: uid('sl'), network: 'github', url: 'https://github.com/' },
                  ],
                } as Block,
              ],
            },
            {
              id: uid('col'),
              blocks: [
                { id: uid('hd'), type: 'heading', level: 3, text: 'Product' } as Block,
                {
                  id: uid('nm'), type: 'nav_menu', source: 'custom', align: 'left', mobile: 'none',
                  items: [
                    { id: uid('n'), label: 'Features', href: '#features' },
                    { id: uid('n'), label: 'Pricing', href: '#pricing' },
                    { id: uid('n'), label: 'FAQ', href: '#faq' },
                  ],
                } as Block,
              ],
            },
            {
              id: uid('col'),
              blocks: [
                { id: uid('hd'), type: 'heading', level: 3, text: 'Company' } as Block,
                {
                  id: uid('nm'), type: 'nav_menu', source: 'custom', align: 'left', mobile: 'none',
                  items: [
                    { id: uid('n'), label: 'About', href: '/about' },
                    { id: uid('n'), label: 'Contact', href: '#contact' },
                  ],
                } as Block,
              ],
            },
            {
              id: uid('col'),
              blocks: [
                {
                  id: uid('nl'), type: 'newsletter',
                  heading: 'Stay in the loop',
                  placeholder: 'you@example.com', buttonLabel: 'Subscribe',
                  align: 'left',
                  destination: 'submissions',
                  success: { message: 'Thanks — you are on the list.' },
                } as Block,
              ],
            },
          ],
        } as Block,
        { id: uid('div'), type: 'divider' } as Block,
        {
          id: uid('p'), type: 'paragraph',
          text: `© ${new Date().getFullYear()} Your company. All rights reserved.`,
          style: { align: 'center', typography: { fontSize: 14 }, textColor: 'var(--pg-muted)' },
        } as Block,
      ],
    }),
  },
]

export const TEMPLATE_CATEGORIES = [
  'Hero', 'Features', 'Social proof', 'Pricing', 'Content', 'Conversion', 'Footer',
] as const
